import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import { Store } from 'components/common/types/Store.types';
import { useSelector } from 'react-redux';
import { isReadOnly } from 'utils/isReadOnly';
import {
  getEveryLineState,
  getIsDirectSalesSessionReadOnly,
  getIsLineBeingSaved,
  getIsLineCancelled,
  getIsLineLocked,
} from 'store/dealManagement/selectors';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';
import { getHasDealReadOnlyPermission } from 'store/dealManagement/selectors/getHasDealReadOnlyPermission';
import { CampaignType } from './PlannerSections/types';

interface UseIsReadOnly {
  isLineReadOnly: boolean;
  isCampaignReadOnly: boolean;
}

const useIsReadOnly = ({
  lineId,
  isEditingAllowed,
}: {
  lineId?: string;
  isEditingAllowed?: boolean;
} = {}): UseIsReadOnly => {
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const hasDealReadOnlyPermission = useSelector(getHasDealReadOnlyPermission);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const campaignType = useSelector((state: Store) => state.dealManagement.campaignType);
  const isDirectSalesSessionLockedAndReadOnly = useSelector(getIsDirectSalesSessionReadOnly);
  const isLineLocked = useSelector((state: Store) => getIsLineLocked(state, lineId));
  const isSavingLine = useSelector((state: Store) => getIsLineBeingSaved(state, lineId));
  const isLineCancelled = useSelector((state: Store) => getIsLineCancelled(state, lineId));
  const lineStates = useSelector(getEveryLineState);
  const isCampaignCancelled =
    lineStates.length > 0 && lineStates.every((state) => state.status === DirectSalesStatusOption.CANCELLED);

  const readOnly = isReadOnly(bookingStatusCode, isEditingDisabled, false, isEditingAllowed);

  if (campaignType !== CampaignType.DIRECT)
    return {
      isLineReadOnly: hasDealReadOnlyPermission || readOnly,
      isCampaignReadOnly: hasDealReadOnlyPermission || readOnly,
    };

  return {
    isLineReadOnly:
      hasDealReadOnlyPermission ||
      isEditingDisabled ||
      isLineCancelled ||
      isDirectSalesSessionLockedAndReadOnly ||
      isLineLocked ||
      isSavingLine,
    isCampaignReadOnly:
      hasDealReadOnlyPermission || isEditingDisabled || isDirectSalesSessionLockedAndReadOnly || isCampaignCancelled,
  };
};

export default useIsReadOnly;
